import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowRight, BadgeCheck, Sparkles, HandCoins, Network, Mail, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { MatchRing } from "../components/kit";

const BENEFITS = [
  [BadgeCheck, "Verified Business Identity", "Your official 10XGLOBAL membership certificate & ID"],
  [Sparkles, "AI-Matched Opportunities", "Buyers, suppliers and partners matched to your business"],
  [HandCoins, "Capital Connections", "Investors and lenders inside the network"],
  [Network, "Global B2B Network", "Verified members across industries and markets"],
];

export default function InvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/invite/${token}`).then((r) => setInvite(r.data)).catch(() => setNotFound(true));
  }, [token]);

  if (notFound) return <div className="max-w-xl mx-auto px-8 py-32 text-center text-slate-400" data-testid="invite-not-found">This invitation link is invalid or has expired.</div>;
  if (!invite) return <div className="max-w-xl mx-auto px-8 py-32 text-sm font-mono text-slate-500 animate-pulse">Loading your invitation…</div>;

  const submitRequest = async () => {
    setSubmitting(true);
    try {
      await api.post(`/invite/${token}/request`);
    } catch (e) { /* already processed — continue */ }
    navigate(`/secure-register?invite=${token}&email=${encodeURIComponent(invite.contact_email)}&company=${encodeURIComponent(invite.company_name)}`);
  };

  return (
    <div className="relative overflow-hidden" data-testid="invite-page">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,rgba(212,175,55,0.10),transparent_55%)]" />
      <div className="relative max-w-3xl mx-auto px-4 md:px-8 py-16 md:py-24 text-center">
        <p className="font-display text-2xl font-black"><span className="text-gold">10X</span><span className="text-slate-100">GLOBAL</span></p>
        <p className="font-mono text-[9px] tracking-[0.4em] text-slate-500 mt-1">A D V I S O R Y</p>

        <p className="gold-kicker mt-12">Personalised Invitation</p>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-100 mt-5 leading-tight" data-testid="invite-headline">
          {invite.company_name},
          <span className="block text-gold mt-2">YOU'RE INVITED TO THE NETWORK.</span>
        </h1>
        <p className="text-slate-400 mt-5 max-w-xl mx-auto leading-relaxed">
          Our intelligence engine identified your company as a strong fit for 10XGLOBAL — the B2B intelligence & growth network.
        </p>

        <div className="card-surface hairline-gold p-8 mt-10 inline-flex flex-col items-center" data-testid="invite-score-card">
          <MatchRing value={invite.ai_score} size={110} testid="invite-score-ring" />
          <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mt-3">10X AI Opportunity Score</p>
          {invite.ai_reason && <p className="text-xs text-slate-400 mt-2 max-w-sm italic">"{invite.ai_reason}"</p>}
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mt-10 text-left" data-testid="invite-benefits">
          {BENEFITS.map(([Icon, t, d]) => (
            <div key={t} className="card-surface p-5 flex items-start gap-3.5">
              <Icon size={20} className="text-gold shrink-0 mt-0.5" />
              <div><p className="text-sm font-semibold text-slate-100">{t}</p><p className="text-xs text-slate-500 mt-1">{d}</p></div>
            </div>
          ))}
        </div>

        <button onClick={submitRequest} disabled={submitting} data-testid="invite-submit-request-btn"
          className="btn-gold mt-12 !px-10 !py-4 text-base disabled:opacity-60">
          {submitting ? <><Loader2 size={16} className="animate-spin" /> SUBMITTING…</> : <>SUBMIT MEMBERSHIP REQUEST <ArrowRight size={18} /></>}
        </button>
        <p className="text-[11px] text-slate-500 mt-4">You'll verify your email & mobile, then our admin team approves your membership.</p>
        <p className="text-xs text-slate-500 mt-8 flex items-center justify-center gap-2">
          <Mail size={13} className="text-gold" /> Official desk: <span className="font-mono text-gold">rathod@10x-global.com</span>
        </p>
        <p className="text-[10px] text-slate-600 mt-3 max-w-md mx-auto leading-relaxed">
          This invitation was sent to the public business contact published on the company's official website. Prefer not to be contacted? <a href="/opt-out" className="text-gold">Opt out instantly</a>.
        </p>
      </div>
    </div>
  );
}
